/**
 * ExpenseForm Component
 *
 * A comprehensive form for creating and editing expenses.
 * Uses useExpenseFormState hook for state management.
 */

import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tabipla/ui";
import { CURRENCIES, getCurrencySymbol, getCurrencyShortName } from "@tabipla/shared";
import { Cloud, CloudOff, AlertTriangle } from "lucide-react";
import { useShake } from "@/application/hooks/useShake";
import { DraftBanner } from "@tabipla/components";
import { EXPENSE_CATEGORIES, CATEGORY_LIST, getCategoryClasses } from "@/config/categories";
import { memberKey, memberJoinedAfterExpenseDate } from "@/utils/expense-form";
import type { ExpenseFormProps } from "./types";
import { useExpenseFormState } from "./useExpenseFormState";

// Helper functions for avatar display
const getInitials = (name: string): string => {
  if (!name?.trim()) return "?";
  if (/[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/.test(name)) {
    return name.slice(0, 2);
  }
  return name
    .split(" ")
    .filter((n) => n.length > 0)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const getAvatarColor = (name: string): string => {
  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-yellow-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-teal-500",
    "bg-orange-500",
  ];
  const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

export function ExpenseForm({
  open,
  onOpenChange,
  members,
  currentUserId,
  onSubmit,
  isSubmitting,
  sessionId,
  editingExpense,
  quickMode = false,
}: ExpenseFormProps) {
  const { t } = useTranslation();
  const { isShaking, triggerShake } = useShake();

  const {
    draft,
    setDraft,
    showDraftBanner,
    setShowDraftBanner,
    saveStatus,
    isEditMode,
    selectedMembers,
    hasUserContent,
    totalCustomAmount,
    parsedAmount,
    isValid,
    handleRestoreDraft,
    handleDiscardDraft,
    toggleMember,
    handleCustomAmountChange,
    selectAllMembers,
    clearAllMembers,
    buildSubmitData,
    clearDraft,
  } = useExpenseFormState({
    open,
    members,
    currentUserId,
    sessionId,
    editingExpense,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Trigger shake if custom split doesn't match
    if (!draft.splitEqually && totalCustomAmount !== parsedAmount) {
      triggerShake();
      return;
    }

    const submitData = buildSubmitData(t);
    if (!submitData) return;

    onSubmit(submitData);
    clearDraft();
  };

  const handleClose = () => {
    setShowDraftBanner(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="flex max-h-[85vh] flex-col sm:max-h-[85vh]"
      >
        <DialogHeader className="shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle>
              {isEditMode ? t("expenses.editExpense", "出費を編集") : t("expenses.addExpense")}
            </DialogTitle>
            {saveStatus !== "idle" && hasUserContent && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {saveStatus === "saving" ? (
                  <>
                    <CloudOff className="h-3 w-3" />
                    <span>{t("draft.saving")}</span>
                  </>
                ) : (
                  <>
                    <Cloud className="h-3 w-3 text-green-500" />
                    <span>{t("draft.saved")}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </DialogHeader>

        {showDraftBanner && (
          <DraftBanner onRestore={handleRestoreDraft} onDiscard={handleDiscardDraft} />
        )}

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-4">
            {/* Amount & Currency */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("expenses.amount")}</label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  inputMode="numeric"
                  value={draft.amount}
                  onChange={(e) => setDraft((prev) => ({ ...prev, amount: e.target.value }))}
                  placeholder={t("expenses.amountPlaceholder")}
                  min="1"
                  required
                  className="flex-1"
                />
                <Select
                  value={draft.currency}
                  onValueChange={(value) =>
                    setDraft((prev) => ({
                      ...prev,
                      currency: value as typeof draft.currency,
                    }))
                  }
                >
                  <SelectTrigger className="w-20 shrink-0">
                    <SelectValue placeholder="円">
                      {getCurrencyShortName(draft.currency)}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.symbol} {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("expenses.description")}</label>
              <Input
                value={draft.description}
                onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
                placeholder={t("expenses.descriptionPlaceholder")}
              />
            </div>

            {/* Category - Icon Grid (hidden in quick mode) */}
            {!quickMode && (
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("expenses.category")}</label>
                <div className="grid grid-cols-3 gap-2">
                  {CATEGORY_LIST.map((cat) => {
                    const config = EXPENSE_CATEGORIES[cat];
                    const IconComponent = config.icon;
                    const isSelected = draft.category === cat;

                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setDraft((prev) => ({ ...prev, category: cat }))}
                        className={`flex flex-col items-center gap-1.5 rounded-lg border-2 p-3 transition-all ${
                          isSelected
                            ? `border-primary ${getCategoryClasses(cat)}`
                            : "border-transparent bg-muted/50 hover:bg-muted"
                        }`}
                      >
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-full ${
                            isSelected ? "bg-white/50 dark:bg-black/20" : getCategoryClasses(cat)
                          }`}
                        >
                          <IconComponent className="h-5 w-5" />
                        </div>
                        <span className="text-xs font-medium">
                          {t(`expenses.categories.${cat}`)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Date (hidden in quick mode) */}
            {!quickMode && (
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("expenses.date")}</label>
                <Input
                  type="date"
                  value={draft.date}
                  onChange={(e) => setDraft((prev) => ({ ...prev, date: e.target.value }))}
                  required
                />
              </div>
            )}

            {/* Payer selection (always visible to enable proxy registration) */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("expenses.paidBy")}</label>
              <div className="flex flex-wrap gap-2">
                {members.map((member) => {
                  const key = memberKey(member.memberId, member.memberType);
                  const isSelected = draft.payerKey === key;
                  const isCurrentUser =
                    member.memberId === currentUserId && member.memberType === "user";

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setDraft((prev) => ({ ...prev, payerKey: key }))}
                      className={`flex items-center gap-2 rounded-full border-2 px-3 py-1.5 transition-all ${
                        isSelected
                          ? "border-primary bg-primary/10"
                          : "border-transparent bg-muted/50 hover:bg-muted"
                      }`}
                    >
                      {/* Avatar with fallback: image > emoji+color > initials */}
                      {member.image ? (
                        <img
                          src={member.image}
                          alt={member.memberName}
                          className="h-6 w-6 rounded-full object-cover"
                        />
                      ) : (
                        <div
                          className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold text-white ${
                            member.avatarColor || getAvatarColor(member.memberName)
                          }`}
                        >
                          {member.avatarEmoji || getInitials(member.memberName)}
                        </div>
                      )}
                      <span className="text-sm font-medium">
                        {member.memberName}
                        {isCurrentUser && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({t("expenses.you")})
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Split type (hidden in quick mode) */}
            {!quickMode && (
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("expenses.splitWith")}</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={draft.splitEqually}
                      onChange={() => setDraft((prev) => ({ ...prev, splitEqually: true }))}
                    />
                    <span className="text-sm">{t("expenses.splitEqually")}</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={!draft.splitEqually}
                      onChange={() => setDraft((prev) => ({ ...prev, splitEqually: false }))}
                    />
                    <span className="text-sm">{t("expenses.customSplit")}</span>
                  </label>
                </div>
              </div>
            )}

            {/* Member selection (hidden in quick mode) */}
            {!quickMode && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">{t("expenses.members", "メンバー")}</label>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={selectAllMembers}
                      className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-200 dark:bg-emerald-900/50 dark:text-emerald-300 dark:hover:bg-emerald-900"
                    >
                      {t("expenses.selectAll", "全員")}
                    </button>
                    <button
                      type="button"
                      onClick={clearAllMembers}
                      className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                    >
                      {t("expenses.selectNone", "解除")}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  {members.map((member) => {
                    const key = memberKey(member.memberId, member.memberType);
                    const expenseDate = new Date(draft.date);
                    const memberJoinedAt = new Date(member.joinedAt);
                    const joinedAfterExpense = memberJoinedAfterExpenseDate(
                      memberJoinedAt,
                      expenseDate
                    );

                    return (
                      <div
                        key={key}
                        className={`flex items-center justify-between rounded-lg border p-2 ${
                          joinedAfterExpense && selectedMembers.has(key)
                            ? "border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30"
                            : ""
                        }`}
                      >
                        <label className="flex min-w-0 flex-1 items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedMembers.has(key)}
                            onChange={() => toggleMember(key)}
                            className="shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <span className="text-sm">
                              {member.memberName}
                              {member.memberId === currentUserId &&
                                member.memberType === "user" && (
                                  <span className="ml-1 text-xs text-muted-foreground">
                                    ({t("expenses.you")})
                                  </span>
                                )}
                            </span>
                            {joinedAfterExpense && (
                              <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                                <AlertTriangle className="h-3 w-3" />
                                <span>
                                  {t("expenses.joinedAfter", {
                                    date: memberJoinedAt.toLocaleDateString("ja-JP", {
                                      month: "short",
                                      day: "numeric",
                                    }),
                                    defaultValue: "{{date}}に参加",
                                  })}
                                </span>
                              </div>
                            )}
                          </div>
                        </label>
                        {draft.splitEqually ? (
                          <span className="shrink-0 text-sm text-muted-foreground">
                            {selectedMembers.has(key) && parsedAmount > 0
                              ? `${getCurrencySymbol(draft.currency)}${Math.floor(parsedAmount / selectedMembers.size).toLocaleString()}`
                              : "-"}
                          </span>
                        ) : (
                          <Input
                            type="number"
                            inputMode="numeric"
                            className="w-24 shrink-0"
                            value={draft.customAmounts[key] || ""}
                            onChange={(e) => handleCustomAmountChange(key, e.target.value)}
                            placeholder="0"
                            disabled={!selectedMembers.has(key)}
                            min="0"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Split summary */}
                {parsedAmount > 0 && selectedMembers.size > 0 && (
                  <div
                    className={`rounded-lg border p-3 ${
                      draft.splitEqually || totalCustomAmount === parsedAmount
                        ? "border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30"
                        : "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30"
                    } ${isShaking ? "animate-shake" : ""}`}
                  >
                    {draft.splitEqually ? (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-sm font-medium">
                          <span>{t("expenses.perPerson", "1人あたり")}</span>
                          <span className="text-lg tabular-nums text-emerald-600 dark:text-emerald-400">
                            {getCurrencySymbol(draft.currency)}
                            {Math.floor(parsedAmount / selectedMembers.size).toLocaleString()}
                          </span>
                        </div>
                        {parsedAmount % selectedMembers.size !== 0 && (
                          <p className="text-xs text-muted-foreground">
                            {t("expenses.remainderNote", {
                              count: parsedAmount % selectedMembers.size,
                              defaultValue: "※端数{{count}}は一部のメンバーに+1",
                            })}
                          </p>
                        )}
                        <div className="flex items-center justify-between border-t border-emerald-200 pt-2 text-xs text-muted-foreground dark:border-emerald-800">
                          <span>
                            {t("expenses.splitCount", {
                              count: selectedMembers.size,
                              defaultValue: "{{count}}人で分割",
                            })}
                          </span>
                          <span>
                            {t("expenses.total")}: {getCurrencySymbol(draft.currency)}
                            {parsedAmount.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-sm font-medium">
                          <span>{t("expenses.inputTotal", "入力合計")}</span>
                          <span
                            className={`text-lg tabular-nums ${
                              totalCustomAmount === parsedAmount
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-red-600 dark:text-red-400"
                            }`}
                          >
                            {getCurrencySymbol(draft.currency)}
                            {totalCustomAmount.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{t("expenses.targetAmount", "目標金額")}</span>
                          <span>
                            {getCurrencySymbol(draft.currency)}
                            {parsedAmount.toLocaleString()}
                          </span>
                        </div>
                        {totalCustomAmount !== parsedAmount && (
                          <p className="text-xs font-medium text-red-600 dark:text-red-400">
                            {totalCustomAmount > parsedAmount
                              ? t("expenses.overAmount", {
                                  symbol: getCurrencySymbol(draft.currency),
                                  amount: (totalCustomAmount - parsedAmount).toLocaleString(),
                                  defaultValue: "{{symbol}}{{amount}}オーバー",
                                })
                              : t("expenses.shortAmount", {
                                  symbol: getCurrencySymbol(draft.currency),
                                  amount: (parsedAmount - totalCustomAmount).toLocaleString(),
                                  defaultValue: "{{symbol}}{{amount}}不足",
                                })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="shrink-0 border-t pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting || !isValid}>
              {isSubmitting
                ? isEditMode
                  ? t("expenses.saving", "保存中...")
                  : t("expenses.creating")
                : isEditMode
                  ? t("expenses.save", "保存")
                  : t("expenses.addExpense")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
